import type { JSX } from 'react';
import type { CategoryDef, DashboardPayload } from '../../dashboard-types.js';
import { cardClassFor, deltaClass, deltaText, type Severity } from '../utils.js';

interface Props {
  data: DashboardPayload;
  sortedCategories: CategoryDef[];
  passesFilter: (c: CategoryDef) => boolean;
  categoryTotal: (c: CategoryDef) => number;
  categoryDelta: (c: CategoryDef) => number;
  categoryWarnCountSizeTaste: (c: CategoryDef) => number;
  severityClass: (v: number) => Severity;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business カテゴリ別タブ。各カテゴリ 1 カードで合計型数とカテゴリ内最大比バー、閾値割れ件数、平均丈・brand_select 率を表示
 * @graph-connects none
 */
export function CardsCategory(props: Props): JSX.Element {
  const list = props.sortedCategories.filter(props.passesFilter);
  if (list.length === 0) {
    return (
      <div className="cards">
        <p style={{ padding: 20, color: 'var(--text-muted)' }}>該当するカテゴリがありません</p>
      </div>
    );
  }
  const max = Math.max(...props.data.categories.map(props.categoryTotal), 1);
  return (
    <div className="cards">
      {list.map((c) => {
        const total = props.categoryTotal(c);
        const delta = props.categoryDelta(c);
        const warnRows = props.categoryWarnCountSizeTaste(c);
        const sev = props.severityClass(total);
        const pct = Math.round((total / max) * 100);
        return (
          <div key={c.id} className={`card ${cardClassFor(sev)}`}>
            <div className="card-head">
              <div className="card-head-left">
                <div className={`cat-icon ${c.cls}`}>{c.short}</div>
                <div className="cat-meta">
                  <span className="cat-name">{c.name}</span>
                  <span className="cat-id">
                    category_id: {c.id} / 平均丈: {c.avgLength}cm ({c.lengthField})
                  </span>
                </div>
              </div>
              <div className="card-summary">
                <span>
                  <b>{total.toLocaleString()}</b>
                  <small> 型</small>
                </span>
                <span className={`delta ${deltaClass(delta)}`}>{deltaText(delta)}</span>
              </div>
            </div>
            <div className="row-list">
              <div className={`pat-row ${sev}`}>
                <span className="size-badge">合計</span>
                <div className="pat-bar-wrap">
                  <div className="pat-bar-track">
                    <div className="pat-bar-fill" style={{ width: `${String(pct)}%` }}></div>
                  </div>
                  <div className="pat-bar-meta">
                    <span>カテゴリ内最大値比 {pct}%</span>
                    <span>閾値割れ size×taste: {warnRows}件</span>
                  </div>
                </div>
                <div className="pat-count">
                  <b>{total.toLocaleString()}</b>
                  <small>型</small>
                  <span className={`delta ${deltaClass(delta)}`}>{deltaText(delta)}</span>
                </div>
              </div>
            </div>
            <div className="card-foot">
              <span className="threshold-info">
                平均丈 <code>{c.avgLength}cm</code> / brand_select率{' '}
                <code>{Math.round(c.brandSelectShare * 100)}%</code>
              </span>
              <span>
                category_id × size × taste: {props.data.sizes.length * props.data.tastes.length}
                スロット
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
